import { Timestamp } from '../firebase/firebaseTypes'
// 사용처: farmfree-functions, farmfree-admin

/**
 * Timestamp를 Date 객체로 변환
 * - Firebase Timestamp (toDate() 메서드 있음)
 * - IndexedDB 복원 형태 ({ seconds, nanoseconds })
 * - 이미 Date 객체
 * - 기타 변환 가능한 형식
 */
export function toDate(timestamp: any): Date {
  if (!timestamp) {
    return new Date()
  }

  // 이미 Date 객체인 경우
  if (timestamp instanceof Date) {
    return timestamp
  }

  // Firebase Timestamp 객체 (toDate() 메서드 있음)
  if (typeof timestamp.toDate === 'function') {
    return timestamp.toDate()
  }

  // IndexedDB 복원 형태: { seconds, nanoseconds, type? }
  if (
    typeof timestamp === 'object' &&
    'seconds' in timestamp &&
    typeof timestamp.seconds === 'number'
  ) {
    const nanoseconds = timestamp.nanoseconds || 0
    return new Date(timestamp.seconds * 1000 + Math.floor(nanoseconds / 1000000))
  }

  // Firebase 클라이언트 형태: { _seconds, _nanoseconds }
  if (
    typeof timestamp === 'object' &&
    '_seconds' in timestamp &&
    typeof timestamp._seconds === 'number'
  ) {
    const nanoseconds = timestamp._nanoseconds || 0
    return new Date(timestamp._seconds * 1000 + Math.floor(nanoseconds / 1000000))
  }

  // 기타: Date 생성자로 파싱 시도
  try {
    return new Date(timestamp)
  } catch {
    return new Date()
  }
}

/**
 * Timestamp 직렬화 객체 타입
 * HTTP API 응답에서 받는 Timestamp 형식
 */
interface SerializedTimestamp {
  _seconds?: number
  seconds?: number
  _nanoseconds?: number
  nanoseconds?: number
}

/**
 * 직렬화된 Timestamp를 Firebase Timestamp 객체로 복원
 */
function restoreTimestampField(field: any): Timestamp | any {
  if (!field || typeof field !== 'object') {
    return field
  }

  const serialized = field as SerializedTimestamp
  if (serialized._seconds !== undefined || serialized.seconds !== undefined) {
    const seconds = serialized._seconds ?? serialized.seconds ?? 0
    const nanoseconds = serialized._nanoseconds ?? serialized.nanoseconds ?? 0
    return new Timestamp(seconds, nanoseconds)
  }

  return field
}

/**
 * HTTP API 응답에서 직렬화된 Timestamp 필드들을 Firebase Timestamp로 복원
 * OrderPen 및 기타 엔티티의 Timestamp 필드들을 자동으로 복원
 *
 * @param obj - Timestamp 필드를 포함하는 객체
 * @returns Timestamp가 복원된 객체
 *
 * @example
 * const order = restoreTimestamps(apiResponse)
 * // order.dateCreated, order.dateModified 등이 Timestamp 객체로 복원됨
 */
export function restoreTimestamps<T extends Record<string, any>>(obj: T): T {
  const result = obj as any

  // dateCreated 복원
  if (result.dateCreated) {
    result.dateCreated = restoreTimestampField(result.dateCreated)
  }

  // dateModified 복원
  if (result.dateModified) {
    result.dateModified = restoreTimestampField(result.dateModified)
  }

  // deliveryItems의 Timestamp 복원
  if (result.deliveryItems && Array.isArray(result.deliveryItems)) {
    result.deliveryItems = result.deliveryItems.map((item: any) => {
      if (item.dateCreated) {
        item.dateCreated = restoreTimestampField(item.dateCreated)
      }
      if (item.dateModified) {
        item.dateModified = restoreTimestampField(item.dateModified)
      }
      return item
    })
  }

  // paymentInfo.lastUpdated 복원
  if (result.paymentInfo?.lastUpdated) {
    result.paymentInfo.lastUpdated = restoreTimestampField(result.paymentInfo.lastUpdated)
  }

  // paymentInfo.matches[].matchedDate 복원
  if (result.paymentInfo?.matches && Array.isArray(result.paymentInfo.matches)) {
    result.paymentInfo.matches = result.paymentInfo.matches.map((match: any) => {
      if (match.matchedDate) {
        match.matchedDate = restoreTimestampField(match.matchedDate)
      }
      if (match.dateCreated) {
        match.dateCreated = restoreTimestampField(match.dateCreated)
      }
      return match
    })
  }

  // matchInfo.lastUpdated 복원 (BankTransaction)
  if (result.matchInfo?.lastUpdated) {
    result.matchInfo.lastUpdated = restoreTimestampField(result.matchInfo.lastUpdated)
  }

  return result
}
